/**
 * ConsentBlock — the one consent UI used at every capture point.
 *
 * Same promise, same wording, same channels everywhere. The parent owns the
 * value so it can submit consent alongside its own form; `ConsentBlock` only
 * renders and reports changes. Recording to the ledger is done with
 * `useConsentRecorder` (below) so no caller has to remember the plumbing.
 */
import { useCallback, useEffect, useState } from "react";
import { Check } from "lucide-react";
import {
  CHANNEL_COPY,
  CONSENT_BODY,
  CONSENT_HEADLINE,
  CONSENT_POLICY_VERSION,
  MESSAGING_CHANNELS,
  type ConsentChannel,
} from "@/lib/consent/channels";
import { recordConsentChannels } from "@/lib/consent-log.functions";
import { getAnonymousId } from "@/lib/identity/anonymousId";
import { enablePush, isPushSupported } from "@/lib/push/client";

export type ConsentSelection = Partial<Record<ConsentChannel, boolean>>;

/**
 * Promotional opt-ins start UNCHECKED, everywhere. Permission to send offers
 * is never pre-selected and is never a condition of buying or pre-reserving;
 * order, delivery and pre-reservation updates are sent regardless.
 */
export function defaultConsentSelection(channels: ConsentChannel[] = MESSAGING_CHANNELS): ConsentSelection {
  return channels.reduce<ConsentSelection>((acc, c) => ({ ...acc, [c]: false }), {});
}

type Props = {
  value: ConsentSelection;
  onChange: (next: ConsentSelection) => void;
  /** Which channels to offer. Defaults to WhatsApp + email + browser. */
  channels?: ConsentChannel[];
  variant?: "inline" | "compact" | "full";
  className?: string;
  /** Override the shared headline (kept identical by default). */
  headline?: string;
  /** Explicit scope wording for a separately versioned company-brand choice. */
  body?: string;
  /** Where the push permission prompt was triggered from (for reporting). */
  pushSource?: string;
  /** True when consent says granted but no live subscription exists here. */
  pushSubscriptionActive?: boolean;
};

/** What the browser itself allows right now. Resolved client-side only. */
type PushCapability = "unknown" | "supported" | "blocked" | "unsupported";

export function ConsentBlock({
  value,
  onChange,
  channels = MESSAGING_CHANNELS,
  variant = "inline",
  className,
  headline = CONSENT_HEADLINE,
  body = CONSENT_BODY,
  pushSource = "consent_block",
  pushSubscriptionActive,
}: Props) {
  const [pushCapability, setPushCapability] = useState<PushCapability>("unknown");
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) {
      setPushCapability("unsupported");
      return;
    }
    setPushCapability(Notification.permission === "denied" ? "blocked" : "supported");
  }, []);

  const toggle = async (channel: ConsentChannel) => {
    const turningOn = !value[channel];

    // Push is the only channel where "on" means real browser work: request
    // native permission and create a subscription. Never record granted
    // without something behind it.
    if (channel === "push" && turningOn) {
      if (pushCapability !== "supported" || pushBusy) return;
      setPushBusy(true);
      const result = await enablePush(pushSource);
      setPushBusy(false);
      if (result === "subscribed") {
        onChange({ ...value, push: true });
      } else {
        if (result === "denied") setPushCapability("blocked");
        if (result === "unsupported") setPushCapability("unsupported");
        onChange({ ...value, push: false });
      }
      return;
    }

    onChange({ ...value, [channel]: !value[channel] });
  };

  const visibleChannels = channels.filter(
    (c) => c !== "push" || pushCapability !== "unsupported",
  );
  const compact = variant === "compact";


  return (
    <div
      className={
        "rounded-2xl border border-border bg-card/70 p-3.5 " + (className ?? "")
      }
      role="group"
      aria-label="Communication preferences"
    >
      <p className="text-[13px] font-semibold text-foreground">{headline}</p>
      <p className="mt-1 text-[12px] leading-snug text-muted-foreground">{body}</p>

      <div className={compact ? "mt-2.5 flex flex-wrap gap-2" : "mt-3 space-y-2"}>
        {visibleChannels.map((channel) => {
          const copy = CHANNEL_COPY[channel];
          const isPush = channel === "push";
          const blocked = isPush && pushCapability === "blocked";
          const on = value[channel] === true && !blocked;
          const disabled = isPush && (blocked || pushBusy || pushCapability === "unknown");
          const pushNote = blocked
            ? "Blocked in your browser settings, allow notifications for this site to turn it on."
            : isPush && on && pushSubscriptionActive === false
              ? "On, but not active on this device."
              : null;
          if (compact) {
            return (
              <button
                key={channel}
                type="button"
                onClick={() => void toggle(channel)}
                disabled={disabled}
                title={pushNote ?? undefined}
                aria-pressed={on}
                className={
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 " +
                  (on
                    ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.08)] text-[hsl(var(--brand))]"
                    : "border-border text-muted-foreground hover:border-[hsl(var(--brand)/0.4)]")
                }
              >
                {on && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                {copy.label}
              </button>
            );
          }
          return (
            <label
              key={channel}
              className={
                (variant === "full" ? "flex min-h-12 items-start gap-3 py-2 text-sm leading-snug text-muted-foreground " : "flex items-start gap-2.5 text-[12px] leading-snug text-muted-foreground ") +
                (disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer")
              }
            >
              <input
                type="checkbox"
                checked={on}
                disabled={disabled}
                onChange={() => void toggle(channel)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border accent-[hsl(var(--brand))]"
              />
              <span>
                <span className="font-medium text-foreground">{copy.label}</span>
                {variant === "full" && <span className="block text-[11.5px]">{copy.hint}</span>}
                {pushNote && (
                  <span className="block text-[11.5px] text-[hsl(var(--brand))]">{pushNote}</span>
                )}
              </span>
            </label>
          );
        })}

      </div>
    </div>
  );
}

/**
 * Persist a consent selection to the ledger. Never throws — a failure to log
 * must never block the customer's journey.
 *
 * `explicit` defaults to true because this hook is normally called straight
 * from a customer's own toggle. Pass `{ explicit: false }` for derived or
 * background writes (a browser that blocks notifications, a default state
 * being mirrored): those are recorded in history but can never withdraw a
 * consent the customer already gave.
 */
export function useConsentRecorder(source: string) {
  return useCallback(
    async (selection: ConsentSelection, options?: { explicit?: boolean }) => {
      const anonymousId = getAnonymousId();
      if (!anonymousId || anonymousId.length < 8) return;
      const entries = (Object.entries(selection) as [ConsentChannel, boolean | undefined][])
        .filter(([, v]) => typeof v === "boolean")
        .map(([channel, v]) => ({ channel, status: v ? ("granted" as const) : ("denied" as const) }));
      if (entries.length === 0) return;
      try {
        await recordConsentChannels({
          data: {
            anonymousId,
            source,
            explicit: options?.explicit !== false,
            policyVersion: CONSENT_POLICY_VERSION,
            pageUrl: typeof window !== "undefined" ? window.location.origin : undefined,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : undefined,
            channels: entries,
          },
        });
      } catch {
        /* logging must never block */
      }
    },
    [source],
  );
}
